import { Sentry } from '@opendoor/observability';
import moment from 'moment-timezone';

import {
  OdProtosListingsData_Listing_State as LISTING_STATE,
  OdProtosAgentsData_AgentLead_State,
  OdProtosListingsData_PARN_State as PARN_STATE,
} from '../__generated__/athenaGlobalTypes';
import { ParnDetails_odProtosListingsServicesPartneragentreferralnetwork_getPARNDetails_parnDetails as ParnDetails } from '../graphql/athena/queries/__generated__/ParnDetails';
import {
  dollarsToCurrencyString,
  formatPhoneNumberInput,
  isNullDateString,
} from '../helpers/formatUtils';

export const NULL_FIELD_DISPLAY = '-';

export interface parnDataProps {
  parnId?: string;
  agentLeadId?: string;
  token?: string;
  listingId?: string;
  listingPrice?: number;
  contractPrice?: number;
  soldPrice?: number;
  grossCommission?: number;
  agentNote?: string;
  pendingAssignmentAcceptance?: boolean;
  parnTimestampsMap?: Map<string, string>;
  listingStateEntryTimes?: Map<LISTING_STATE, string>;
  proposalExpiration?: Date;
  parnState?: PARN_STATE;
  listingState?: LISTING_STATE;
  agentLeadState?: OdProtosAgentsData_AgentLead_State;
}
/** Customer data, usually at least customer name address and email would be present */
export interface customerDataProps {
  name: string;
  phone?: string;
  email?: string;
  address: string;
}

export interface addressDataProps {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export interface LeadObject {
  [key: string]: ParnLead;
}

export interface ParnLead {
  parnData: parnDataProps;
  addressData: addressDataProps;
  customerData: customerDataProps;
}

export function convertParnDetails(parnDetails: ParnDetails): ParnLead | undefined {
  const lead = parnDetails?.lead;

  // Address data
  const address = parnDetails?.lead?.address;
  const addressData = {
    street: address?.street,
    city: address?.city,
    state: address?.state,
    postalCode: address?.postalCode,
  };
  // Customer data
  const customer = lead?.customerV2;
  const email =
    customer?.primaryEmail?.address?.value || customer?.otherEmails?.[0]?.address?.value;
  const customerData = {
    name: `${customer?.firstName} ${customer?.lastName}`,
    phone: customer?.primaryPhoneNumber?.number?.value
      ? formatPhoneNumberInput(customer?.primaryPhoneNumber?.number?.value)
      : 'N/A',
    email: email ?? 'N/A',
    address: `${address?.street}, ${address?.city}, ${address?.state}, ${address?.postalCode}`,
  };
  let parnData;
  try {
    parnData = buildParnData(parnDetails);
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setExtra('parnDetails', JSON.stringify(parnDetails));
      scope.setExtra('error', JSON.stringify(error));
      Sentry.captureException(error);
    });
    return undefined;
  }
  return {
    parnData,
    customerData,
    addressData,
  };
}

//Formats date like 'January 10, 2023'
export const getReadableDate = (date: string | undefined): string => {
  return date && !isNullDateString(date)
    ? `${moment(date).format('MMMM D, YYYY')}`
    : NULL_FIELD_DISPLAY;
};

export const getReadableStreetFromParnLead = (addressData: ParnLead['addressData']) => {
  return `${addressData.street},`;
};

export const getReadableCityFromParnLead = (addressData: ParnLead['addressData']) => {
  return `${addressData.city}, ${addressData.state}, ${addressData.postalCode}`;
};

export const getReadableExpirationFromParnLead = (
  date: Date | moment.Moment | undefined,
): string => {
  const localTime = moment.utc(date).local();
  const timezoneAbbr = moment().tz(moment.tz.guess()).format('z');
  return localTime.format(`hh:mm A [${timezoneAbbr}]`);
};

//Coalesces and returns the relevant parn, listing and agent data
export function buildParnData(parnDetails: ParnDetails) {
  if (!parnDetails.parn || parnDetails.parn.id == null) {
    throw new Error('buildParnData: PARN ID is undefined or null');
  }

  const parn = parnDetails.parn;
  const listing = parnDetails.listing;
  const agentLead = parnDetails.agentLead;

  const pendingAssignmentAcceptance =
    agentLead?.state === OdProtosAgentsData_AgentLead_State.AGENTS_PROPOSED;

  const parnTimestampsMap = new Map<string, string>();
  (parn?.timestamps as Array<{ key: string; timestamp: string }>).forEach((timestamp) => {
    const date = new Date(timestamp.timestamp);
    const localDateString = date.toLocaleDateString();
    parnTimestampsMap.set(timestamp.key, localDateString);
  });

  const listingStateEntryTimes = new Map<LISTING_STATE, string>();
  (listing?.timestamps as Array<{ state: LISTING_STATE; timestamp: string }>).forEach(
    (timestamp) => {
      const date = new Date(timestamp.timestamp);
      const localDateString = date.toLocaleDateString();
      listingStateEntryTimes.set(timestamp.state, localDateString);
    },
  );

  const parnData = {
    parnId: parn.id,
    agentLeadId: agentLead?.id,
    token: parn?.sellDirectLeadToken,
    listingId: listing?.id,
    listingPrice: listing?.listingPrice,
    contractPrice: listing?.state === LISTING_STATE.LISTING_ACTIVE ? 0 : listing?.contractPrice,
    soldPrice: listing?.soldPrice,
    grossCommission: listing?.grossCommission,
    agentNote: parn?.agentNote,
    pendingAssignmentAcceptance: pendingAssignmentAcceptance,
    parnTimestampsMap,
    listingStateEntryTimes,
    proposalExpiration: agentLead?.proposalExpiration,
    parnState: parn?.state,
    listingState: listing?.state,
    agentLeadState: agentLead?.state,
  };
  return parnData;
}

export function getReadableCurrency(price?: number, defaultDisplay?: string): string | undefined {
  return price && price !== 0
    ? dollarsToCurrencyString(price, false)
    : defaultDisplay ?? 'Not set yet';
}
